/**
 * Share-grant API layer — the FE side of the "public view-only shareable
 * links" feature.
 *
 * Two distinct trust boundaries, two distinct transports:
 *
 *   - mint/list/revoke are AUTHED admin-level campaign operations. They go
 *     through the shared `apiClient` (Firebase Bearer injected, `/api/v1`
 *     base) exactly like every other campaign-detail write — see
 *     `campaignQueries.ts` / `adminMutations.ts` for the sibling patterns.
 *
 *   - `fetchPublicShareView` hits the PUBLIC, unauthenticated
 *     `/share-view/{camp_code}/{tail}` endpoint at the API's ROOT (no
 *     `/api/v1`, no Bearer token — an anonymous visitor has no Firebase
 *     session to attach one from). `apiClient` always injects a token when
 *     one exists and always prefixes `/api/v1`, so this deliberately uses a
 *     raw `fetch` instead. `apiBase` is a constructor-injected PARAM, never
 *     read from an app-local helper (`getApiBaseUrl` lives in
 *     apps/brand/src/lib and packages/features must not import app code) —
 *     the page passes it in.
 *
 * Wire shapes mirror the Go DTOs character-for-character (snake_case) — see
 * internal/campaigns/share_grants_handler.go (ShareGrantDto) and
 * share_view_handler.go / share_view_creator.go / share_view_page.go (the
 * public envelope). The one deliberate exception is the grant descriptor
 * embedded in the public envelope: the FE-side masked shell
 * (`PublicShareGrantContext` in @discovr/ui) reads it in camelCase, so
 * `fetchPublicShareView` maps that one sub-object on the way in. Everything
 * else in the public payload (campaign context + script/content/safety/
 * insights/roster/reporting) passes through unchanged.
 *
 * `PublicShareGrantContext` is re-declared here (not imported from
 * @discovr/ui) because packages/ui depends on packages/features, not the
 * other way around — see packages/ui/package.json's `@discovr/features`
 * dependency. Importing it from ui here would create a circular package
 * dependency. Keep this shape in sync with
 * packages/ui/src/campaign-detail/publicView.tsx's `PublicShareGrantContext`.
 */
import { apiClient, readVisitId } from '@discovr/common';
import type {
  DataResponse,
  ScriptReviewAiEnvelope,
  GeoAiEnvelope,
  ScriptDoc,
  ShopifyStoreState,
} from '@discovr/types';
import type { ShopifyDiscountKindTerms } from '../../shopify/discountKind';

// ─── Authed share-grant DTOs (mint/list/revoke) ─────────────────────────────

/** Wire shape of one share grant — mirrors shareGrantDTO in share_grants_handler.go. */
export interface ShareGrantDto {
  id: string;
  key: string;
  kind: 'creator' | 'page';
  username?: string;
  purpose?: string;
  sub?: string;
  tab?: string;
  pill?: string;
  page?: number;
  page_size?: number;
  total_pages?: number;
  /** Reporting-window preset (Task 12) — `'1w'|'1m'|'6m'|'full'` — only ever
   *  set for a grant minted on one of the two windowed reporting virtual
   *  slugs (general-insights, sentiment). Mirrors the Go DTO's
   *  `json:"window,omitempty"`: absent on the wire when empty, so undefined
   *  here means "no window" — every non-reporting grant, and the two other
   *  reporting virtual slugs, always have this undefined. */
  window?: string;
  /** Column sort baked into the grant at mint time — mirrors the FE's own
   *  ?sort_by/?sort_dir URL fields (see useRosterTabSort). Absent/empty
   *  `sort_by` = unsorted (default recency), same convention as the live URL.
   *  Only ever set for a page-kind grant on one of the sortable roster tabs. */
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  created_at: string;
  expires_at: string;
  revoked_at?: string;
  status: 'active' | 'expired' | 'revoked';
  /** Whether an anonymous visitor must supply a password to view this grant.
   *  Added by BE Task 9 (share_grants_handler.go). */
  password_protected: boolean;
  /** D5 toggle's current state for this grant — never omitted, `false` is
   *  meaningful and common. */
  allow_chat_readonly: boolean;
  /** Number of times this link's public view has been fetched — click
   *  analytics. Always present on the wire, never omitted (0 for a link
   *  with no clicks yet) — mirrors shareGrantDTO.ClickCount's
   *  `json:"click_count"` (no omitempty). */
  click_count: number;
  /** ISO timestamp of the most recent click, if any. Absent (undefined) on
   *  the wire when the link has never been opened — mirrors
   *  shareGrantDTO.LastViewedAt's `json:"last_viewed_at,omitempty"`. */
  last_viewed_at?: string;
}

/** TTL presets the redesigned share dialog offers — Task 9. Wire values match
 *  the Go handler's accepted strings exactly (share_grants_handler.go). */
export type ShareGrantTtl = '24h' | '7d' | '30d';

/** Mirrors mintShareGrantRequest in share_grants_handler.go. `ttl` is always
 *  sent (no BE-side default); `password` is present only when the caller is
 *  minting a password-protected grant.
 *
 *  `window` (Task 12) is REQUIRED — the Go request struct has no `omitempty`
 *  on this field, so every mint call must send it explicitly, even when
 *  empty: `''` for "no window" (every screen except the two windowed
 *  reporting virtual slugs), or one of `'1w'|'1m'|'6m'|'full'` when minting
 *  from general-insights/sentiment with the CURRENT window selection. The BE
 *  400s on a non-empty `window` for any other screen. */
export interface MintShareGrantInput {
  tail: string;
  ttl: ShareGrantTtl;
  window: string;
  password?: string;
  page_size?: number;
  total_pages?: number;
  /** Current column sort at mint time — same optional/omit-when-unsorted
   *  convention as page_size/total_pages. Absent = unsorted; the BE then
   *  clears any previously-recorded sort on a re-mint (mint is authoritative
   *  over every field it accepts, same as password/window). */
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  /** D5: only meaningful (and only ever sent as `true`) when `tail` resolves
   *  to a script/content/chat-purpose creator grant — the BE 400s otherwise.
   *  Omitted (not `false`) on every other mint, matching how `password` is
   *  only sent when set. Named snake_case, matching `page_size`/
   *  `total_pages` above: this interface's fields go straight onto the
   *  request body with no camelCase→snake_case mapping step. */
  allow_chat_readonly?: boolean;
}

/**
 * POST /api/v1/campaigns/{id}/share-grants
 *
 * Mints (or re-mints) a public, view-only share grant for one campaign-detail
 * screen. Admin-level access required (campaign:share:write) — enforced BE-side.
 */
export async function mintShareGrant(
  campaignId: string,
  input: MintShareGrantInput,
): Promise<ShareGrantDto> {
  const res = await apiClient.post<DataResponse<ShareGrantDto>>(
    `/campaigns/${encodeURIComponent(campaignId)}/share-grants`,
    input,
  );
  return res.data.data;
}

/**
 * GET /api/v1/campaigns/{id}/share-grants
 *
 * Every share grant ever minted on the campaign (incl. revoked/expired),
 * newest first — BE-sorted.
 */
export async function listShareGrants(campaignId: string): Promise<ShareGrantDto[]> {
  const res = await apiClient.get<DataResponse<ShareGrantDto[]>>(
    `/campaigns/${encodeURIComponent(campaignId)}/share-grants`,
  );
  return res.data.data;
}

/**
 * DELETE /api/v1/campaigns/{id}/share-grants/{grantId} → 204
 *
 * Revokes a share grant. Never deletes the underlying document BE-side
 * (only stamps revoked_at) — the FE just needs the call to succeed.
 */
export async function revokeShareGrant(campaignId: string, grantId: string): Promise<void> {
  await apiClient.delete(
    `/campaigns/${encodeURIComponent(campaignId)}/share-grants/${encodeURIComponent(grantId)}`,
  );
}

// ─── Link funnel summary ─────────────────────────────────────────────────────

export interface LinkFunnelSummaryDto {
  total_opens: number;
  device_split: { mobile: number; desktop: number; tablet: number };
  public_views: number;
  authed: number;
  public_to_login_conversions: number;
  login_wall_drop_off_rate: number;
}

/**
 * GET /api/v1/campaigns/{id}/link-funnel-summary
 *
 * Device split, public/private split, login-wall drop-off, and
 * public-to-login conversions for this campaign's shareable links.
 * Admin-level access required (campaign:share:read) — enforced BE-side.
 */
export async function getLinkFunnelSummary(
  campaignId: string,
  window?: string,
): Promise<LinkFunnelSummaryDto> {
  const query = window ? `?window=${encodeURIComponent(window)}` : '';
  const res = await apiClient.get<DataResponse<LinkFunnelSummaryDto>>(
    `/campaigns/${encodeURIComponent(campaignId)}/link-funnel-summary${query}`,
  );
  return res.data.data;
}

/**
 * Request body for `updateShareGrant` — mirrors `updateShareGrantTTLRequest`
 * in share_grants_handler.go. `ttl` is REQUIRED on every call (no BE-side
 * default, unlike mint) — send the grant's current/closest preset even when
 * only the password is changing. `password` and `remove_password` are
 * mutually exclusive: the BE 400s if both are set on the same request
 * ("cannot set and remove a password in the same request"), so callers must
 * never send both — see EditShareGrantDialog for the UI-level guard.
 */
export interface UpdateShareGrantInput {
  ttl: ShareGrantTtl;
  /** Non-empty regenerates the link's password. Omitted/empty leaves any
   *  existing password untouched (does NOT clear it). */
  password?: string;
  /** `true` clears an existing password back to unprotected. Must not be
   *  combined with a non-empty `password` in the same call. */
  remove_password?: boolean;
}

/**
 * Response body for `updateShareGrant` — mirrors the `resp` map built in
 * `UpdateExpiry` (share_grants_handler.go). `password_protected` is only
 * present when the request definitively changed it (removed, or set/
 * regenerated) — absent on a TTL-only edit, since the BE doesn't know the
 * pre-existing state without an extra read. Callers must not assume it's
 * always present; invalidate-and-refetch the grants list instead of trusting
 * this field alone.
 */
export interface UpdateShareGrantResult {
  ttl: ShareGrantTtl;
  expires_at: string;
  password_updated: boolean;
  password_protected?: boolean;
}

/**
 * PATCH /api/v1/campaigns/{id}/share-grants/{grantId}
 *
 * Edits a share grant's TTL (always resets expiry to now + ttl) and,
 * optionally, its password (regenerate via `password`, or clear via
 * `remove_password`). Admin-level access required (campaign:share:write) —
 * enforced BE-side. 409 if the grant has been revoked.
 */
export async function updateShareGrant(
  campaignId: string,
  grantId: string,
  input: UpdateShareGrantInput,
): Promise<UpdateShareGrantResult> {
  const res = await apiClient.patch<DataResponse<UpdateShareGrantResult>>(
    `/campaigns/${encodeURIComponent(campaignId)}/share-grants/${encodeURIComponent(grantId)}`,
    input,
  );
  return res.data.data;
}

// ─── Public share-view DTOs (anonymous, unauthenticated) ────────────────────

/**
 * Camel-cased grant descriptor the masked shell consumes. Mirrors
 * @discovr/ui's `PublicShareGrantContext` (packages/ui/src/campaign-detail/
 * publicView.tsx) — kept as a local, structurally-identical type rather than
 * an import because packages/features must not depend on packages/ui (see
 * this file's header). `fetchPublicShareView` is the only place that
 * produces one, mapping it off the wire's snake_case `grant` object.
 */
export interface PublicShareGrantContext {
  kind: 'creator' | 'page';
  username?: string;
  purpose?: string;
  sub?: string;
  tab?: string;
  pill?: string;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  /** Reporting-window preset (Task 12) the grant was minted for —
   *  `'1w'|'1m'|'6m'|'full'` — present only for a general-insights/sentiment
   *  grant. The public renderer shows this as a static, non-interactive
   *  label; it never re-derives or re-slices a window client-side. */
  window?: string;
  expiresAt: string;
}

/** Mirrors publicCampaignContext in share_view_handler.go. */
export interface PublicCampaignContext {
  name: string;
  short_code: string;
  platform: string;
}

/** Mirrors publicCreatorHeader in share_view_creator.go. */
export interface PublicCreatorHeader {
  username: string;
  name?: string;
  avatar_url?: string;
  platform: string;
}

/** Mirrors publicScriptView in share_view_creator.go. AIAnalysis/GeoAnalysis
 *  carry the SAME envelope shape as ScriptDetail.ai_analysis/.geo_analysis —
 *  the BE proxies the identical AI-service content to this public path
 *  (2026-08-05 parity decision). `status` is only ever "pending"/"complete"/
 *  "failed" on the wire (never "processing"), so ScriptReviewAiEnvelope's
 *  narrower union is exact, not just assignable. */
export interface PublicScriptView {
  creator: PublicCreatorHeader;
  status: string;
  /** The script's native document tree (Lexical), rendered read-only. Every
   *  script is doc-JSON; there is no markdown/plain-text fallback. */
  doc?: ScriptDoc;
  version: number;
  updated_at: string;
  ai_analysis?: ScriptReviewAiEnvelope;
  geo_analysis?: GeoAiEnvelope;
  /** D5/D7: present only when this grant's allow_chat_readonly was true at
   *  mint time AND the thread has messages — omitted (not `[]`) otherwise,
   *  so "toggle off" and "toggle on, empty thread" are indistinguishable on
   *  the wire (both render no chat column — see PublicScriptPanel). */
  thread_messages?: PublicThreadMessage[];
}

/** Mirrors publicContentView in share_view_creator.go. AIAnalysis mirrors
 *  ScriptDetail's — see PublicScriptView's doc comment. `version` (added by
 *  BE PR #359, `buildContentView`) mirrors `PublicScriptView.version` — it
 *  lets a resumed `approve_content` pending action be staleness-checked
 *  against `ContentDetail.version` the same way `approve_script` already is
 *  (see pendingPublicActionRegistry.ts's `createApprovePhaseHandler`). */
export interface PublicContentView {
  creator: PublicCreatorHeader;
  status: string;
  url?: string;
  asset_url?: string;
  kind?: string;
  version: number;
  updated_at: string;
  ai_analysis?: ScriptReviewAiEnvelope;
  geo_analysis?: GeoAiEnvelope;
  /** D5/D7: present only when this grant's allow_chat_readonly was true at
   *  mint time AND the thread has messages — omitted (not `[]`) otherwise,
   *  so "toggle off" and "toggle on, empty thread" are indistinguishable on
   *  the wire (both render no chat column — see PublicScriptPanel). */
  thread_messages?: PublicThreadMessage[];
}

/** Mirrors publicThreadMessage in share_view_creator.go — six fields only.
 *  The BE deliberately never sends a message id, thread id, or the
 *  sender's internal user id (this file's own "no ObjectIDs anywhere"
 *  allow-list rule), so this type must not declare them either — see
 *  toReviewThreadEntries below for how a stable local key is derived
 *  without one. */
export interface PublicThreadMessage {
  sender_role: string;
  sender_name: string;
  sender_title?: string;
  body: string;
  /** Omitted (not `''`) when absent on the wire — BE's `kind` field is
   *  `omitempty`. Empty/omitted means "chat", per ReviewThreadEntryKind's
   *  own doc comment. */
  kind?: string;
  created_at: string;
}

/** Mirrors publicConversationView in share_view_creator.go — the 'chat'
 *  share purpose's payload (D6). */
export interface PublicConversationView {
  creator: PublicCreatorHeader;
  messages?: PublicThreadMessage[];
}

/** Maps the wire shape to the shape ChatThread/CreatorReviewPanel already
 *  consume (@discovr/types' ReviewThreadEntry) — `sender_role` becomes
 *  `actor` (drives left/right alignment + role label). `ReviewThreadEntry.id`
 *  is required (it's a shared type — the authed script/content threads DO
 *  carry a real message id), but the public wire never carries one, so a
 *  stable local key is synthesized from `created_at` + index instead of
 *  passing through an id that doesn't exist. */
export function toReviewThreadEntries(
  messages: PublicThreadMessage[] | undefined,
): import('@discovr/types').ReviewThreadEntry[] | undefined {
  if (!messages) return undefined;
  return messages.map((m, i) => ({
    id: `${m.created_at}-${i}`,
    created_at: m.created_at,
    actor: m.sender_role,
    body: m.body,
    sender_name: m.sender_name,
    sender_title: m.sender_title,
    kind: (m.kind ?? 'chat') as import('@discovr/types').ReviewThreadEntryKind,
  }));
}

/** Mirrors publicSafetyFlag in share_view_creator.go. */
export interface PublicSafetyFlag {
  category: string;
  severity: string;
  note?: string;
}

/** Mirrors publicSafetyView in share_view_creator.go. Score/summary/flags
 *  populate only once a verdict's job status reaches "complete" — but per
 *  the 2026-08-19 "shortcode is the protection, not a compute gate" policy,
 *  the public path fires the SAME enqueue/fetch as an authed view (via
 *  resolveSafetyState, shared on the BE) when no verdict exists yet, so a
 *  never-analyzed creator's first anonymous share-link visit can itself
 *  trigger a fresh AI computation — these fields simply stay absent while
 *  that job is still pending. */
export interface PublicSafetyView {
  creator: PublicCreatorHeader;
  status: string;
  score?: number | null;
  /** The AI service's own risk classification (safe/moderate/risky/blocked/
   *  needs_review) — render this directly instead of re-deriving a band
   *  from `score` alone. Tier and score aren't purely correlated (a
   *  hard-block hit can carry a moderate numeric score), so a re-derived
   *  band can contradict `summary`, which is synthesized from this same
   *  tier on the BE. */
  tier?: string;
  summary?: string;
  flags?: PublicSafetyFlag[];
}

/** One {gender, age_range} audience-share bucket — mirrors
 *  publicGenderAgeShare in share_view_creator.go. All three fields are
 *  required on the wire (no `omitempty` on any of them BE-side). */
export interface PublicGenderAgeShare {
  gender: string;
  age_range: string;
  value: number;
}

/** One {gender} audience-share bucket (no age breakdown) — mirrors
 *  publicGenderShare. Required fields, same as PublicGenderAgeShare. */
export interface PublicGenderShare {
  gender: string;
  value: number;
}

/** A {code, value} audience-share pair — used for countries (ISO code) and
 *  languages (language code). Mirrors publicCodeShare. */
export interface PublicCodeShare {
  code: string;
  value: number;
}

/** A {name, value} audience-share pair — used for states, cities, and
 *  interests (a display name rather than a code). Mirrors publicNamedShare.
 *  NOT the same shape as PublicCodeShare — countries/languages key off
 *  `code`, states/cities/interests key off `name`; callers rendering both
 *  through one shared list component supply their own label extractor. */
export interface PublicNamedShare {
  name: string;
  value: number;
}

/** Aggregate, anonymous audience stats for one creator — no individual
 *  follower/liker identities, ever (mirrors the authed AnalyticsTab's
 *  Audience Demographics section, reduced to a public-safe subset). Mirrors
 *  publicAudienceDemographics in share_view_creator.go exactly — every field
 *  is optional (nil slice omitted on the wire), and each list independently
 *  may be empty when that one dimension has no data. */
export interface PublicInsightsDemographics {
  gender_age_distribution?: PublicGenderAgeShare[];
  gender_distribution?: PublicGenderShare[];
  countries?: PublicCodeShare[];
  states?: PublicNamedShare[];
  cities?: PublicNamedShare[];
  languages?: PublicCodeShare[];
  interests?: PublicNamedShare[];
}

/** One month of a creator's follower/subscriber-count and engagement
 *  history — mirrors publicGrowthPoint in share_view_creator.go. `month` is
 *  the only field without `omitempty` BE-side (a growth point is never
 *  emitted without one); every numeric field, INCLUDING `average_likes` (a
 *  non-pointer `float64` with `json:"average_likes,omitempty"` — Go still
 *  omits a zero-valued non-pointer numeric field from the wire), is optional
 *  here to match. `follower_count` is populated for Instagram creators,
 *  `subscriber_count` for YouTube — never both on the same point. */
export interface PublicGrowthPoint {
  month: string;
  follower_count?: number;
  subscriber_count?: number;
  average_likes?: number;
  average_comments?: number;
  average_views?: number;
  total_likes?: number;
  total_views?: number;
}

/** One content item's per-post engagement counters — mirrors
 *  publicContentEngagement. Every field is a non-pointer/pointer numeric
 *  with `omitempty`, so each is independently optional on the wire (e.g. a
 *  YouTube video may carry `views`/`likes`/`comments` but never
 *  `plays`/`shares`/`saves`, which are Instagram-only concepts). */
export interface PublicContentEngagement {
  likes?: number;
  comments?: number;
  views?: number;
  plays?: number;
  shares?: number;
  saves?: number;
}

/** One post from a creator's recent/top/sponsored content history — mirrors
 *  publicContentHistoryItem, reused identically for `recent_content`,
 *  `top_content`, and `sponsored_content` on PublicInsightsView.
 *  `published_at` is required (no `omitempty` BE-side); every string field
 *  is optional. `engagement` is a non-pointer struct field with
 *  `omitempty` — Go's `encoding/json` never omits non-pointer STRUCT fields
 *  regardless of `omitempty` (only booleans/numbers/strings/pointers/
 *  interfaces/slices/maps/arrays honor it), so `engagement` is always
 *  present on the wire even when every counter inside it is absent. */
export interface PublicContentHistoryItem {
  type?: string;
  url?: string;
  title?: string;
  caption?: string;
  thumbnail_url?: string;
  published_at: string;
  engagement: PublicContentEngagement;
}

/** Mirrors publicInsightsView in share_view_creator.go. `demographics` /
 *  `growth_history` / `recent_content` / `top_content` / `sponsored_content`
 *  (all optional/nullable) are the 2026-08 "insights parity" creator-
 *  analysis fields — a public link scoped to `purpose: 'insights'` for this
 *  creator now carries the same audience demographics, follower/subscriber
 *  growth history, and recent/top/sponsored content lists the authed brand
 *  Creator Analysis page shows, minus anything admin-only (pricing/GMV,
 *  contact details, lookalike/notable-follower profiles — never sent to
 *  brand users either, so there's no new gap here). Any/all of the five may
 *  be absent (older link, or no cached Phyllo doc yet for this creator) —
 *  render a graceful fallback, never crash on a missing group. */
export interface PublicInsightsView {
  creator: PublicCreatorHeader;
  followers?: number;
  engagement_rate?: number;
  avg_views?: number;
  sub?: string;
  demographics?: PublicInsightsDemographics;
  growth_history?: PublicGrowthPoint[];
  recent_content?: PublicContentHistoryItem[];
  top_content?: PublicContentHistoryItem[];
  sponsored_content?: PublicContentHistoryItem[];
}

/** Mirrors publicRosterDeliverable in share_view_page.go. The BE guarantees a
 *  row's array is never a mix of selected/unselected: when the entry has at
 *  least one selected deliverable, ONLY the selected one(s) are emitted (all
 *  `is_selected: true`); when none are selected, EVERY available option is
 *  emitted (all `is_selected: false`) as a price-preview catalogue.
 *  `base_price` is entirely absent on the wire (not `0`) when there's no
 *  price to show — never treat an absent value as a zero price. */
export interface PublicRosterDeliverable {
  type: string;
  base_price?: number;
  is_selected: boolean;
  is_primary?: boolean;
}

/** Mirrors publicRosterRow in share_view_page.go. estimated_budget/
 *  estimated_cpv are the row's OWN negotiation-time figures (2026-08-05
 *  parity decision) — never a table-wide aggregate, which this endpoint
 *  never queries. Omitted (not `0`) when genuinely unset, same convention as
 *  `deliverables[].base_price`. */
export interface PublicRosterRow {
  username: string;
  name?: string;
  avatar_url?: string;
  status: string;
  followers?: number;
  avg_views?: number;
  engagement_rate?: number;
  deliverables?: PublicRosterDeliverable[];
  live_url?: string;
  estimated_budget?: number;
  estimated_cpv?: number;
}

/** Mirrors publicRosterPage in share_view_page.go. */
export interface PublicRosterPage {
  tab: string;
  pill?: string;
  page: number;
  total_pages: number;
  rows: PublicRosterRow[];
}

/** Mirrors publicReportingView in share_view_page.go — Series is a minimal
 *  per-sub (label/value-shaped) array, never a KPI aggregate. */
export interface PublicReportingView {
  sub: string;
  series: unknown[];
}

/**
 * Mirrors publicDetailsCreatorPreference/publicDetailsDemographics/
 * publicDetailsBrief/publicDetailsProduct/publicDetailsView in
 * share_view_page.go, field-for-field — same names as the FE's own
 * `Campaign` type's corresponding fields (see that type's doc comment for
 * why: CampaignDetailsCards.tsx is reused unmodified, fed a
 * partial-`Campaign`-shaped object assembled straight from this payload).
 */
export interface PublicDetailsView {
  name: string;
  description?: string;
  platform: string;
  primary_goal?: string;
  total_budget?: number;
  payout_model?: string;
  status: string;
  deliverables_text?: string;
  deliverables?: string[];
  usage_rights_days?: number;
  creator_preference: {
    creator_count: number;
    categories?: string[];
    tiers?: string[];
    creator_genders?: string[];
    gender_notes?: string;
  };
  demographics: {
    age_min?: number;
    age_max?: number;
    genders?: string[];
    languages?: string[];
    regions?: string[];
  };
  brief: {
    primary_focus?: string;
    video_title?: string;
    video_description?: string;
    secondary_focus?: string;
    cta?: string;
    dos?: string[];
    donts?: string[];
    sample_video_url?: string;
  };
  products?: Array<{ name: string; product_url?: string; notes?: string }>;
}

/** Mirrors shopify.DiscountTerms — the discount-terms snapshot a coupon was
 *  actually minted under (see publicShopifyCoupon's Terms field). */
export interface PublicShopifyDiscountTerms extends ShopifyDiscountKindTerms {
  coupon_name: string;
  value_type?: string;
  discount_pct: number;
  discount_amount?: number;
  include_pct_in_code: boolean;
  target_type: string;
  products?: Array<{ id: string; title: string }>;
  variants?: Array<{ id: string; title: string; product_id: string; product_title: string }>;
  collections?: Array<{ id: string; title: string }>;
  minimum_requirement_type?: string;
  minimum_requirement_value?: number;
  combines_with_order_discounts: boolean;
  combines_with_product_discounts: boolean;
  combines_with_shipping_discounts: boolean;
  start_date?: string;
  end_date?: string;
  /** The store's base currency at mint time. The BE sends the full
   *  domain.DiscountTerms here, so this rides along with everything else. */
  currency_code?: string;
  /** Present for the same reason as currency_code — share_view_page.go embeds
   *  the whole domain.DiscountTerms. Declared so the public tooltip's reads of
   *  them are type-checked rather than resting on an undocumented gap. */
  usage_limit?: number;
  applies_once_per_customer?: boolean;
  applies_on_each_item?: boolean;
  purchase_type?: string;
  recurring_cycle_limit?: number;
}

/** Mirrors publicShopifyCoupon in share_view_page.go — full parity with the
 *  authed Coupons table screen, coupon code included (2026-08-21 product
 *  decision). See that Go type's doc comment for what's excluded and why
 *  (uses_count — modal-only data with no public-mode
 *  counterpart). */
export interface PublicShopifyCoupon {
  id: string;
  scope: string;
  code: string;
  tracking_link: string;
  status: string;
  discount_pct: number;
  creator_name?: string;
  creator_handle?: string;
  creator_avatar?: string;
  creator_live: boolean;
  shop_domain: string;
  terms: PublicShopifyDiscountTerms;
}

/** Mirrors shopifyCouponsStatus in share_view_page.go — the Coupons view's
 *  banner subset of the authed config response; see that Go type's doc
 *  comment for exactly which authed-config fields are (and are not)
 *  carried. */
export interface PublicShopifyStatus {
  connected: boolean;
  shop_domain?: string;
  coupon_count: number;
  live_coupon_count: number;
  mode?: string;
  /** The campaign's store state; omitted by older servers. */
  store_state?: ShopifyStoreState;
  /** The connected store's base currency (ISO 4217). The fallback for any
   *  coupon minted before terms carried their own — without it this view,
   *  the one with the widest external audience, would mislabel a non-INR
   *  store's amounts. */
  currency_code?: string;
  /** The store's IANA time zone (e.g. "America/New_York") — the zone coupon
   *  dates are shown and edited in. Omitted when unknown; the FE then uses UTC. */
  store_timezone?: string;
}

/** The page the coupons list is on — `common.WritePaginated`'s block, under
 *  `data.shopify`. `total` counts every coupon matching the filters. */
export interface PublicShopifyPagination {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

/** Mirrors publicShopifyView in share_view_page.go. `coupons` is one page. */
export interface PublicShopifyView {
  status: PublicShopifyStatus;
  coupons: PublicShopifyCoupon[];
  pagination: PublicShopifyPagination;
}

export type PublicShopifyLiveFilter = 'all' | 'live' | 'not_live';

/** Query the share-view route reads for a shopify grant. */
export interface PublicShopifyQuery {
  page: number;
  limit: number;
  live: PublicShopifyLiveFilter;
  search: string;
}

/**
 * The anonymous share-view payload — mirrors publicShareViewResponse in
 * share_view_handler.go, with `grant` mapped to the camelCase
 * PublicShareGrantContext (see that interface's doc comment). No `slug` —
 * the BE deliberately omits it on this path (anonymous viewers never
 * construct canonical /campaign/{slug} URLs).
 */
export interface PublicShareView {
  campaign: PublicCampaignContext;
  grant: PublicShareGrantContext;
  script?: PublicScriptView;
  content?: PublicContentView;
  safety?: PublicSafetyView;
  insights?: PublicInsightsView;
  roster?: PublicRosterPage;
  reporting?: PublicReportingView;
  details?: PublicDetailsView;
  shopify?: PublicShopifyView;
  /** D6: the 'chat' share purpose's payload — present only for a grant
   *  minted with `purpose: 'chat'`. */
  chat?: PublicConversationView;
}

/** Raw wire shape of the `grant` sub-object before camelCase mapping —
 *  mirrors publicGrantContext in share_view_handler.go. */
interface WirePublicGrantContext {
  kind: 'creator' | 'page';
  username?: string;
  purpose?: string;
  sub?: string;
  tab?: string;
  pill?: string;
  page?: number;
  page_size: number;
  total_pages: number;
  window?: string;
  expires_at: string;
}

/** Raw wire shape of the whole envelope before `grant` is mapped. */
interface PublicShareViewWire {
  campaign: PublicCampaignContext;
  grant: WirePublicGrantContext;
  script?: PublicScriptView;
  content?: PublicContentView;
  safety?: PublicSafetyView;
  insights?: PublicInsightsView;
  roster?: PublicRosterPage;
  reporting?: PublicReportingView;
  details?: PublicDetailsView;
  shopify?: PublicShopifyView;
  chat?: PublicConversationView;
}

function toPublicShareGrantContext(g: WirePublicGrantContext): PublicShareGrantContext {
  return {
    kind: g.kind,
    username: g.username,
    purpose: g.purpose,
    sub: g.sub,
    tab: g.tab,
    pill: g.pill,
    page: g.page,
    pageSize: g.page_size,
    totalPages: g.total_pages,
    window: g.window,
    expiresAt: g.expires_at,
  };
}

function mapWireToPublicShareView(wire: PublicShareViewWire): PublicShareView {
  return {
    campaign: wire.campaign,
    grant: toPublicShareGrantContext(wire.grant),
    script: wire.script,
    content: wire.content,
    safety: wire.safety,
    insights: wire.insights,
    roster: wire.roster,
    reporting: wire.reporting,
    details: wire.details,
    shopify: wire.shopify,
    chat: wire.chat,
  };
}

/** Thrown when the public share-view endpoint 404s — the BE collapses every
 *  lookup-miss failure mode (flag off, unknown campaign, unparseable/
 *  non-grantable tail, no active grant) into the same 404, so this error
 *  carries no distinguishing detail either; the caller renders one generic
 *  "this link isn't available" state. */
export class ShareViewNotFoundError extends Error {
  constructor(message = 'Share link not found') {
    super(message);
    this.name = 'ShareViewNotFoundError';
  }
}

/**
 * Thrown by `unlockPublicShareView` on a non-2xx response (401 wrong
 * password, 429 rate-limited, or anything else). `message` is the BE's exact
 * `error.message` text — both the wrong-password copy ("incorrect password")
 * and the rate-limit copy are meant to be rendered verbatim, never
 * re-worded — see share_view_handler.go's unlock route. `status` lets a
 * caller special-case 429 if it ever needs to (e.g. disabling retry) without
 * parsing the message text.
 */
export class UnlockShareViewError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'UnlockShareViewError';
    this.status = status;
  }
}

/** Wire shape of the `password_required` envelope the GET endpoint returns
 *  (still 200) when the grant is password-protected and no valid unlock
 *  cookie was sent. Mirrors the BE's `publicPasswordRequiredResponse` in
 *  share_view_handler.go. */
interface PasswordRequiredWire {
  password_required: true;
  campaign: { name: string };
}

function isPasswordRequiredWire(
  data: PublicShareViewWire | PasswordRequiredWire,
): data is PasswordRequiredWire {
  return 'password_required' in data && data.password_required === true;
}

/**
 * Discriminated result of `fetchPublicShareView` — the GET endpoint's 200
 * response is no longer always the full view: a password-protected grant
 * with no valid unlock cookie now also 200s, carrying only the campaign name
 * so the gate can greet the visitor by campaign before they unlock it.
 */
export type PublicShareViewResult =
  { kind: 'view'; data: PublicShareView } | { kind: 'password_required'; campaignName: string };

/** Wire error envelope shared by every failure response in this codebase —
 *  `{error: {code, message}, trace_id}`. Used to read the 401/429 copy off
 *  the unlock endpoint's non-2xx responses. */
interface WireErrorEnvelope {
  error?: { code?: string; message?: string };
  trace_id?: string;
}

/** A shopify grant's tail ("shopify" or its "shopify-coupons" link slug). Only
 *  those carry the coupon paging params. */
export function isShopifyShareTail(tail: string): boolean {
  return /^shopify(-coupons)?(\/|$)/.test(tail);
}

/** Joins apiBase + /share-view/{campCode}/{tail}[+pathSuffix], encoding each
 *  path segment individually — `tail` is itself a "/"-joined multi-segment
 *  string (e.g. "mkbhd/script"), so a single blanket encodeURIComponent
 *  would wrongly escape its internal slashes. `pathSuffix` (e.g. '/unlock'),
 *  when given, is appended to the PATH *before* the query string is built —
 *  it must never be concatenated onto this function's return value by a
 *  caller, since the returned URL may already carry a `?vid=` query string
 *  and a literal `/unlock` after that would land in the query string instead
 *  of the path (the BE's unlock route requires the tail's last path segment
 *  to be `unlock`). Appends ?vid= from sessionStorage (Baseline Metrics)
 *  LAST, after any path suffix, when present, so a public view that later
 *  converts to a login can be attributed back to the same link tap the
 *  login-wall drop-off path already uses — see visitId.ts. */
function buildPublicShareViewUrl(
  apiBase: string,
  campCode: string,
  tail: string,
  pathSuffix = '',
  query?: PublicShopifyQuery,
): string {
  const base = apiBase.replace(/\/$/, '');
  const encodedCampCode = encodeURIComponent(campCode);
  const encodedTail = tail
    .split('/')
    .filter((seg) => seg.length > 0)
    .map(encodeURIComponent)
    .join('/');
  const url = `${base}/share-view/${encodedCampCode}${encodedTail ? `/${encodedTail}` : ''}${pathSuffix}`;
  const params = new URLSearchParams();
  if (query && isShopifyShareTail(tail)) {
    params.set('page', String(query.page));
    params.set('limit', String(query.limit));
    if (query.live !== 'all') params.set('live', query.live);
    if (query.search.trim()) params.set('search', query.search.trim());
  }
  const vid = readVisitId();
  if (vid) params.set('vid', vid);
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * GET {apiBase}/share-view/{campCode}/{tail}
 *
 * Public, unauthenticated fetch — deliberately a raw `fetch`, not `apiClient`
 * (which always injects a Firebase Bearer token when one exists and always
 * prefixes `/api/v1`; an anonymous visitor must send neither). `apiBase` is a
 * param, not read from an app-local helper — see this file's header.
 *
 * `credentials: 'include'` so the browser sends the BE's HttpOnly unlock
 * cookie (set by `unlockPublicShareView`) on this GET too — otherwise a page
 * reload after a successful unlock would re-hit the password gate.
 *
 * Throws `ShareViewNotFoundError` on a 404 (every lookup-miss mode collapses
 * to 404 BE-side). Throws a plain `Error` on any other non-2xx response —
 * distinct from the 404 case so callers can tell "this link doesn't exist"
 * apart from "something broke". Returns a `PublicShareViewResult` — either
 * the full view (`kind: 'view'`) or, for a password-protected grant with no
 * valid unlock cookie yet, `kind: 'password_required'`.
 *
 * `query` pages and filters the coupons of a shopify grant (page, limit, live,
 * search); other grants ignore it.
 */
export async function fetchPublicShareView(
  apiBase: string,
  campCode: string,
  tail: string,
  query?: PublicShopifyQuery,
): Promise<PublicShareViewResult> {
  const url = buildPublicShareViewUrl(apiBase, campCode, tail, '', query);
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (res.status === 404) {
    throw new ShareViewNotFoundError();
  }
  if (!res.ok) {
    throw new Error(`Share view request failed (${res.status})`);
  }

  const body = (await res.json()) as { data: PublicShareViewWire | PasswordRequiredWire };
  if (isPasswordRequiredWire(body.data)) {
    return { kind: 'password_required', campaignName: body.data.campaign.name };
  }
  return { kind: 'view', data: mapWireToPublicShareView(body.data) };
}

/**
 * POST {apiBase}/share-view/{campCode}/{tail}/unlock
 *
 * Submits the visitor-entered password for a password-protected grant.
 * `credentials: 'include'` is load-bearing here, not decorative: the BE sets
 * `Access-Control-Allow-Credentials: true` on this route specifically so the
 * browser stores the HttpOnly unlock `Set-Cookie` it returns on success —
 * omitting this option silently breaks the cookie round-trip with no
 * client-visible error (the unlock still "succeeds" once, but every
 * subsequent GET/reload re-hits the password gate).
 *
 * On success (200) the response body is the exact same `PublicShareViewWire`
 * shape the GET returns for an unlocked view — mapped through the same
 * `mapWireToPublicShareView` — so a successful unlock can render
 * `PublicCampaignShell` directly, no second GET.
 *
 * On a non-2xx response (401 wrong password, 429 rate-limited, or anything
 * else) throws `UnlockShareViewError` carrying the BE's exact
 * `error.message` text — render it verbatim, never re-worded.
 */
export async function unlockPublicShareView(
  apiBase: string,
  campCode: string,
  tail: string,
  password: string,
): Promise<PublicShareView> {
  const url = buildPublicShareViewUrl(apiBase, campCode, tail, '/unlock');
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ password }),
  });

  if (!res.ok) {
    const errorBody = (await res.json().catch(() => null)) as WireErrorEnvelope | null;
    const message = errorBody?.error?.message ?? `Unable to unlock this link (${res.status})`;
    throw new UnlockShareViewError(message, res.status);
  }

  const body = (await res.json()) as { data: PublicShareViewWire };
  return mapWireToPublicShareView(body.data);
}
